import { useMemo } from "react";

import type { ReceivedGumball, ThemeSceneProps } from "../types";

// Spider-web geometry. The web is tucked into the bottom-right corner of
// the scene — strung from the right edge across to a tree branch on the
// left, with the lower strands fading into the velvet road. Coordinates
// live in the web SVG's viewBox (1000 × 600). The SVG renders with
// preserveAspectRatio="none", so viewBox positions map linearly to
// container percentages and the gumball cards (positioned by % in HTML)
// stay locked onto the web strands at every aspect ratio.
const WEB_CENTER_X = 750;
const WEB_CENTER_Y = 360;
const WEB_RING_RADII = [55, 115, 175];
const WEB_SPOKE_RADIUS = 220;
const WEB_SPOKE_ANGLES = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

function webPoint(r: number, angleDeg: number): { x: number; y: number } {
  const a = (angleDeg * Math.PI) / 180;
  return {
    x: WEB_CENTER_X + r * Math.cos(a),
    y: WEB_CENTER_Y - r * Math.sin(a)
  };
}

// Long anchor strands tethering the web to the edges of the scene.
// One reaches up-left into the trees, two snap to the right edge, and
// one stretches down-right toward the velvet road.
const WEB_ANCHORS: ReadonlyArray<{ from: number; to: { x: number; y: number } }> = [
  { from: 150, to: { x: 300, y: 100 } },  // up-left toward trees / upper-left
  { from: 60, to: { x: 940, y: 50 } },    // up-right toward the sky
  { from: 0, to: { x: 1010, y: 360 } },   // anchored to the right edge
  { from: 330, to: { x: 1010, y: 540 } }  // down-right toward the velvet road
];

// Fixed gumball slots — distributed across the web. Slots stay above
// ~75% top so they remain visible before the opaque velvet road covers
// them. Coordinates are percentages of the scene container (left/top),
// precomputed from `webPoint` (viewBox 1000×600, preserveAspectRatio="none").
const WEB_SLOTS: ReadonlyArray<{ left: number; top: number }> = [
  // Inner ring (r=55) — four cardinal points
  { left: 80.5, top: 60 },     // 0°
  { left: 75, top: 50.83 },    // 90°
  { left: 69.5, top: 60 },     // 180°
  { left: 75, top: 69.17 },    // 270°
  // Middle ring (r=115) — skip 240°/270°/300° (covered by road)
  { left: 86.5, top: 60 },     // 0°
  { left: 84.96, top: 50.42 }, // 30°
  { left: 80.75, top: 43.4 },  // 60°
  { left: 75, top: 40.83 },    // 90°
  { left: 69.25, top: 43.4 },  // 120°
  { left: 65.04, top: 50.42 }, // 150°
  { left: 63.5, top: 60 },     // 180°
  { left: 65.04, top: 69.58 }, // 210°
  { left: 84.96, top: 69.58 }, // 330°
  // Outer ring (r=175) — skip 240°/270°/300° (covered by road)
  { left: 92.5, top: 60 },     // 0°
  { left: 90.16, top: 45.42 }, // 30°
  { left: 83.75, top: 34.74 }, // 60°
  { left: 75, top: 30.83 },    // 90°
  { left: 66.25, top: 34.74 }, // 120°
  { left: 59.85, top: 45.42 }, // 150°
  { left: 57.5, top: 60 },     // 180°
  { left: 59.85, top: 74.58 }, // 210°
  { left: 90.16, top: 74.58 }  // 330°
];

const STARS: ReadonlyArray<readonly [number, number, number]> = [
  [90, 80, 1.4], [180, 140, 1], [260, 40, 0.9], [340, 180, 1.1], [420, 60, 1.2],
  [520, 220, 0.8], [610, 90, 1], [700, 40, 1.3], [820, 200, 0.9], [940, 140, 1.1],
  [1080, 80, 1.2], [1190, 220, 1], [1380, 360, 1.1], [1480, 140, 1],
  [60, 300, 0.9], [140, 420, 1.1], [240, 360, 0.8], [380, 480, 1.2],
  [520, 400, 0.9], [700, 500, 1.1], [860, 440, 1], [1020, 520, 0.9],
  [1140, 380, 1.2], [1300, 560, 1], [1500, 460, 1.1],
  [1560, 260, 0.9], [1440, 40, 0.8], [1240, 300, 1.0], [1340, 200, 0.7]
];

const PETALS: ReadonlyArray<readonly [number, number, number, string]> = [
  [120, 80, 1.4, "#6b1029"],
  [380, 160, 1, "#c8b8d6"],
  [620, 60, 1.6, "#6b1029"],
  [880, 240, 1.2, "#e2a85c"],
  [1180, 100, 1.3, "#c8b8d6"],
  [1480, 200, 1.4, "#6b1029"],
  [240, 320, 1.1, "#c8b8d6"],
  [780, 400, 1.5, "#6b1029"],
  [1340, 380, 1.2, "#e2a85c"],
  [60, 480, 1, "#c8b8d6"],
  [560, 540, 1.3, "#6b1029"],
  [1020, 460, 1.4, "#c8b8d6"]
];

const FLOOR_CANDLES: ReadonlyArray<{
  x: number;
  y: number;
  h: number;
  kind: "red" | "silver" | "violet";
}> = [
  { x: 180, y: 380, h: 56, kind: "red" },
  { x: 380, y: 350, h: 80, kind: "silver" },
  { x: 600, y: 360, h: 64, kind: "red" },
  { x: 900, y: 350, h: 90, kind: "violet" },
  { x: 1140, y: 360, h: 72, kind: "red" },
  { x: 1360, y: 370, h: 60, kind: "silver" }
];

export function MidnightVelvetScene({ received }: ThemeSceneProps) {
  return (
    <div className="mv-scene" aria-hidden="true">
      {/* === Sky === */}
      <svg
        className="mv-scene__sky"
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="mv-sky" cx="80%" cy="18%" r="90%">
            <stop offset="0%" stopColor="#3a1c45" />
            <stop offset="35%" stopColor="#1a0c26" />
            <stop offset="100%" stopColor="#06030c" />
          </radialGradient>
          <radialGradient id="mv-moon" cx="0.4" cy="0.4" r="0.7">
            <stop offset="0%" stopColor="#fbf6ff" />
            <stop offset="55%" stopColor="#e1d3e8" />
            <stop offset="92%" stopColor="#a995b3" />
            <stop offset="100%" stopColor="#5b3e6a" />
          </radialGradient>
          <radialGradient id="mv-halo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgba(236, 230, 240, 0.55)" />
            <stop offset="55%" stopColor="rgba(200, 184, 214, 0.18)" />
            <stop offset="100%" stopColor="rgba(200, 184, 214, 0)" />
          </radialGradient>
        </defs>
        <rect width="1600" height="900" fill="url(#mv-sky)" />

        <ellipse cx="1280" cy="180" rx="320" ry="320" fill="url(#mv-halo)" />

        <g fill="#ece6f0">
          {STARS.map(([x, y, r], i) => (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={r}
              opacity={0.5 + (i % 5) * 0.1}
            >
              <animate
                attributeName="opacity"
                values={`${0.4 + (i % 5) * 0.1};${0.9};${0.4 + (i % 5) * 0.1}`}
                dur={`${2 + (i % 5)}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
          <g stroke="#f6efff" strokeWidth="0.8">
            <path d="M1060 60 L1060 78 M1052 69 L1068 69" />
            <path d="M340 540 L340 556 M332 548 L348 548" />
            <path d="M1480 280 L1480 296 M1472 288 L1488 288" />
            <path d="M620 300 L620 314 M613 307 L627 307" />
          </g>
        </g>

        <g>
          <path
            d="M200 80 Q260 110 320 160"
            stroke="rgba(226, 168, 92, 0.9)"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          >
            <animate attributeName="opacity" values="0;1;0" dur="6s" repeatCount="indefinite" begin="0s" />
          </path>
          <path
            d="M1100 220 Q1180 280 1260 380"
            stroke="rgba(212, 54, 94, 0.9)"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          >
            <animate attributeName="opacity" values="0;1;0" dur="8s" repeatCount="indefinite" begin="2s" />
          </path>
          <path
            d="M740 120 Q820 200 900 320"
            stroke="rgba(236, 230, 240, 0.8)"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
          >
            <animate attributeName="opacity" values="0;1;0" dur="7s" repeatCount="indefinite" begin="4s" />
          </path>
        </g>

        {/* Moon */}
        <g>
          <circle cx="1280" cy="180" r="140" fill="url(#mv-moon)" />
          <g fill="#5b3e6a" opacity="0.30">
            <circle cx="1248" cy="148" r="14" />
            <circle cx="1320" cy="222" r="20" />
            <circle cx="1232" cy="220" r="10" />
            <circle cx="1340" cy="160" r="9" />
            <circle cx="1280" cy="118" r="6" />
          </g>
          <path
            d="M1280 40 a140 140 0 1 1 0 280 a100 140 0 1 0 0 -280 z"
            fill="rgba(11, 6, 18, 0.30)"
          />
        </g>

        {/* Drifting bats */}
        <g fill="#0b0710">
          <g transform="translate(900 220)">
            <path d="M0 0 q-10 -8 -20 0 q7 3 10 10 q3 -8 10 -10 q7 3 10 10 q3 -8 10 -10 q-10 -8 -20 0 z">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 8,-6; 0,0; -8,-3; 0,0"
                dur="1.2s"
                repeatCount="indefinite"
              />
            </path>
          </g>
          <g transform="translate(1140 320)" opacity="0.85">
            <path d="M0 0 q-7 -5 -14 0 q5 2 7 7 q2 -5 7 -7 q5 2 7 7 q2 -5 7 -7 q-7 -5 -14 0 z">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; -6,-4; 0,0; 6,-2; 0,0"
                dur="1.4s"
                repeatCount="indefinite"
              />
            </path>
          </g>
          <g transform="translate(560 280)" opacity="0.7">
            <path d="M0 0 q-6 -4 -12 0 q4 2 6 6 q2 -4 6 -6 q4 2 6 6 q2 -4 6 -6 q-6 -4 -12 0 z">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 5,-3; 0,0; -5,-2; 0,0"
                dur="1.6s"
                repeatCount="indefinite"
              />
            </path>
          </g>
          <g transform="translate(280 380)" opacity="0.6">
            <path d="M0 0 q-5 -3 -10 0 q3 1 4 5 q2 -4 4 -5 q3 1 4 5 q2 -4 4 -5 q-5 -3 -10 0 z" />
          </g>
        </g>

        {/* Cathedral silhouette */}
        <g fill="rgba(8, 4, 14, 0.85)">
          <path
            d="M0 720 L0 580 L40 580 L40 540 Q40 510 65 510 Q90 510 90 540 L90 580 L130 580 L130 480
               Q130 440 165 440 Q200 440 200 480 L200 580 L240 580 L240 540 Q240 510 265 510 Q290 510 290 540
               L290 580 L330 580 L330 720 Z"
          />
        </g>
        <g fill="rgba(226, 168, 92, 0.55)">
          <rect x="60" y="540" width="6" height="14" rx="3" />
          <rect x="156" y="500" width="10" height="20" rx="5" />
          <rect x="186" y="500" width="10" height="20" rx="5" />
          <rect x="260" y="540" width="6" height="14" rx="3" />
          <ellipse cx="165" cy="465" rx="4" ry="6" />
        </g>
      </svg>

      {/* === Mid-ground: bare trees with hanging orb "gumballs" === */}
      <svg
        className="mv-scene__trees"
        viewBox="0 0 1600 600"
        preserveAspectRatio="xMidYEnd slice"
      >
        <defs>
          <linearGradient id="mv-tree" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1a0e22" />
            <stop offset="100%" stopColor="#06030c" />
          </linearGradient>
          <radialGradient id="mv-orb-red" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#d4365e" />
            <stop offset="55%" stopColor="#6b1029" />
            <stop offset="100%" stopColor="#1a0e22" />
          </radialGradient>
          <radialGradient id="mv-orb-silver" cx="0.4" cy="0.4" r="0.6">
            <stop offset="0%" stopColor="#e0d3ea" />
            <stop offset="55%" stopColor="#7d6b8c" />
            <stop offset="100%" stopColor="#1a0e22" />
          </radialGradient>
          <radialGradient id="mv-orb-violet" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#9f7aff" />
            <stop offset="55%" stopColor="#3a1c45" />
            <stop offset="100%" stopColor="#0c0610" />
          </radialGradient>
        </defs>

        <g transform="translate(220 600)">
          <path
            d="M0 0 L0 -280 Q-2 -300 -8 -310 L-10 -340 M0 -200 Q-20 -210 -40 -240 M0 -150 Q18 -158 38 -180
               M0 -100 Q-22 -108 -34 -130 M0 -250 Q14 -260 28 -280"
            stroke="url(#mv-tree)"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
          />
          <line x1="-10" y1="-340" x2="-10" y2="-320" stroke="#1a0e22" strokeWidth="1" />
          <circle cx="-10" cy="-310" r="14" fill="url(#mv-orb-red)" />
        </g>

        <g transform="translate(460 600)">
          <path
            d="M0 0 L0 -200 Q4 -220 10 -228 L8 -260 M0 -120 Q22 -130 44 -150
               M0 -90 Q-18 -100 -36 -120 M0 -180 Q-16 -190 -32 -208"
            stroke="url(#mv-tree)"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
          />
          <line x1="8" y1="-260" x2="8" y2="-244" stroke="#1a0e22" strokeWidth="1" />
          <circle cx="8" cy="-235" r="12" fill="url(#mv-orb-silver)" />
          <line x1="44" y1="-150" x2="44" y2="-138" stroke="#1a0e22" strokeWidth="0.8" />
          <circle cx="44" cy="-130" r="7" fill="url(#mv-orb-red)" opacity="0.85" />
        </g>

        <g transform="translate(800 600)">
          <path
            d="M0 0 L0 -340 Q-6 -360 -14 -370 L-12 -400 M0 -240 Q-26 -250 -52 -276
               M0 -160 Q22 -170 46 -192 M0 -300 Q18 -310 36 -328
               M0 -110 Q-26 -120 -48 -140"
            stroke="url(#mv-tree)"
            strokeWidth="6.5"
            fill="none"
            strokeLinecap="round"
          />
          <line x1="-12" y1="-400" x2="-12" y2="-378" stroke="#1a0e22" strokeWidth="1" />
          <circle cx="-12" cy="-368" r="16" fill="url(#mv-orb-violet)" />
          <line x1="46" y1="-192" x2="46" y2="-178" stroke="#1a0e22" strokeWidth="0.8" />
          <circle cx="46" cy="-168" r="9" fill="url(#mv-orb-red)" opacity="0.9" />
        </g>

        <g transform="translate(1040 600)">
          <path
            d="M0 0 L0 -240 Q4 -260 12 -270 L10 -296 M0 -140 Q24 -150 48 -172
               M0 -100 Q-20 -110 -40 -130 M0 -210 Q-16 -220 -34 -240"
            stroke="url(#mv-tree)"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
          />
          <line x1="10" y1="-296" x2="10" y2="-282" stroke="#1a0e22" strokeWidth="1" />
          <circle cx="10" cy="-272" r="13" fill="url(#mv-orb-red)" />
        </g>

        <g transform="translate(1280 600)">
          <path
            d="M0 0 L0 -200 Q-4 -218 -10 -226 L-12 -256 M0 -130 Q-22 -140 -44 -160
               M0 -90 Q18 -100 36 -120 M0 -180 Q14 -190 28 -208"
            stroke="url(#mv-tree)"
            strokeWidth="4.5"
            fill="none"
            strokeLinecap="round"
          />
          <line x1="-12" y1="-256" x2="-12" y2="-242" stroke="#1a0e22" strokeWidth="1" />
          <circle cx="-12" cy="-232" r="11" fill="url(#mv-orb-silver)" opacity="0.9" />
        </g>

        {/* Drifting ash / petals */}
        <g>
          {PETALS.map(([x, y, r, color], i) => (
            <circle key={i} cx={x} cy={y} r={r} fill={color} opacity="0.7">
              <animate
                attributeName="cy"
                values={`${y};${y + 18};${y}`}
                dur={`${5 + (i % 4)}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.4;0.9;0.4"
                dur={`${3 + (i % 4)}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
        </g>
      </svg>

      {/* === Spider web — strung between the trees. Gumballs hang on it. === */}
      <svg
        className="mv-scene__web"
        viewBox="0 0 1000 600"
        preserveAspectRatio="none"
      >
        <g className="mv-web__strands">
          {/* Long anchor strands tethering the web to nearby branches */}
          {WEB_ANCHORS.map((anchor, i) => {
            const start = webPoint(WEB_SPOKE_RADIUS, anchor.from);
            return (
              <line
                key={`anchor-${i}`}
                x1={start.x.toFixed(2)}
                y1={start.y.toFixed(2)}
                x2={anchor.to.x}
                y2={anchor.to.y}
              />
            );
          })}
          {/* Radial spokes */}
          {WEB_SPOKE_ANGLES.map((angle, i) => {
            const end = webPoint(WEB_SPOKE_RADIUS, angle);
            return (
              <line
                key={`spoke-${i}`}
                x1={WEB_CENTER_X}
                y1={WEB_CENTER_Y}
                x2={end.x.toFixed(2)}
                y2={end.y.toFixed(2)}
              />
            );
          })}
          {/* Concentric rings — slightly sagged using quadratic curves
              between successive spoke endpoints. */}
          {WEB_RING_RADII.map((r, ri) => {
            const pts = WEB_SPOKE_ANGLES.map((a) => webPoint(r, a));
            const sag = 2 + ri * 1.5; // outer rings sag more
            let d = `M ${pts[0]!.x.toFixed(2)} ${pts[0]!.y.toFixed(2)}`;
            for (let i = 0; i < pts.length; i++) {
              const from = pts[i]!;
              const to = pts[(i + 1) % pts.length]!;
              // Midpoint pulled toward the center by `sag` so the strand
              // bows inward — feels lived-in rather than perfectly taut.
              const mx = (from.x + to.x) / 2;
              const my = (from.y + to.y) / 2;
              const dx = mx - WEB_CENTER_X;
              const dy = my - WEB_CENTER_Y;
              const len = Math.hypot(dx, dy) || 1;
              const cx = mx - (dx / len) * sag;
              const cy = my - (dy / len) * sag;
              d += ` Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${to.x.toFixed(2)} ${to.y.toFixed(2)}`;
            }
            return <path key={`ring-${ri}`} d={d} />;
          })}
        </g>
        {/* A few dewdrops glinting on the strands */}
        <g className="mv-web__dew">
          <circle cx="870" cy="305" r="1.4" />
          <circle cx="775" cy="400" r="1.4" />
          <circle cx="918" cy="318" r="1.1" />
          <circle cx="823" cy="318" r="1.1" />
          <circle cx="870" cy="340" r="1.2" />
        </g>
      </svg>

      {/* === Foreground: oxblood velvet road + dark unicorn === */}
      <svg
        className="mv-scene__floor"
        viewBox="0 0 1600 600"
        preserveAspectRatio="xMidYEnd slice"
      >
        <defs>
          <linearGradient id="mv-floor" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#2a0c1a" stopOpacity="0" />
            <stop offset="20%" stopColor="#2a0c1a" stopOpacity="0.6" />
            <stop offset="55%" stopColor="#6b1029" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0b0710" />
          </linearGradient>
          <linearGradient id="mv-stripe-1" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#b91c47" stopOpacity="0" />
            <stop offset="12%" stopColor="#b91c47" stopOpacity="0.85" />
            <stop offset="88%" stopColor="#6b1029" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#2a0c1a" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="mv-stripe-2" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#c8b8d6" stopOpacity="0" />
            <stop offset="15%" stopColor="#c8b8d6" stopOpacity="0.55" />
            <stop offset="85%" stopColor="#c8b8d6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#c8b8d6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="mv-stripe-3" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#e2a85c" stopOpacity="0" />
            <stop offset="15%" stopColor="#e2a85c" stopOpacity="0.7" />
            <stop offset="85%" stopColor="#e2a85c" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#e2a85c" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="mv-unicorn" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#1a0e22" />
            <stop offset="55%" stopColor="#0c0610" />
            <stop offset="100%" stopColor="#3a1c45" />
          </linearGradient>
          <linearGradient id="mv-mane" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#c8b8d6" />
            <stop offset="50%" stopColor="#6b1029" />
            <stop offset="100%" stopColor="#1a0e22" />
          </linearGradient>
          <linearGradient id="mv-horn" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f6efff" />
            <stop offset="55%" stopColor="#c8b8d6" />
            <stop offset="100%" stopColor="#5b3e6a" />
          </linearGradient>
          <radialGradient id="mv-fog" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgba(200, 184, 214, 0.20)" />
            <stop offset="100%" stopColor="rgba(200, 184, 214, 0)" />
          </radialGradient>
        </defs>

        <path
          d="M0 360 Q400 280 800 340 T1600 360 L1600 600 L0 600 Z"
          fill="url(#mv-floor)"
        />

        {/* Velvet ribbons along the road */}
        <path d="M0 380 Q400 300 800 360 T1600 380" stroke="url(#mv-stripe-1)" strokeWidth="14" fill="none" />
        <path d="M0 404 Q400 324 800 384 T1600 404" stroke="url(#mv-stripe-3)" strokeWidth="6" fill="none" opacity="0.75" />
        <path d="M0 426 Q400 346 800 406 T1600 426" stroke="url(#mv-stripe-2)" strokeWidth="3" fill="none" />
        <path d="M0 450 Q400 370 800 430 T1600 450" stroke="url(#mv-stripe-1)" strokeWidth="8" fill="none" opacity="0.6" />

        {/* Fog wisps */}
        <ellipse cx="280" cy="380" rx="240" ry="36" fill="url(#mv-fog)" />
        <ellipse cx="900" cy="370" rx="320" ry="40" fill="url(#mv-fog)" />
        <ellipse cx="1380" cy="400" rx="260" ry="34" fill="url(#mv-fog)" />

        {/* Candle "lollipops" */}
        <g>
          {FLOOR_CANDLES.map((candle, i) => (
            <g key={i} transform={`translate(${candle.x} ${candle.y})`}>
              <rect x="-2" y={-candle.h} width="4" height={candle.h} fill="#1a0e22" />
              <circle
                cx="0"
                cy={-candle.h - 12}
                r="14"
                fill={
                  candle.kind === "red"
                    ? "url(#mv-orb-red)"
                    : candle.kind === "silver"
                      ? "url(#mv-orb-silver)"
                      : "url(#mv-orb-violet)"
                }
              />
              {candle.kind !== "silver" && (
                <ellipse cx="0" cy={-candle.h - 28} rx="3" ry="6" fill="#e2a85c" opacity="0.85">
                  <animate attributeName="ry" values="6;5;6.5;5.5;6" dur="1.6s" repeatCount="indefinite" />
                </ellipse>
              )}
            </g>
          ))}
        </g>

        {/* Dark unicorn rearing */}
        <g transform="translate(1240 400) scale(1.5)">
          <path
            d="M-4 78 Q-32 70 -42 96 Q-28 96 -22 88 Q-18 110 -32 124 Q-8 112 -2 96 Z"
            fill="url(#mv-mane)"
          />
          <path
            d="M22 100 L18 138 Q18 142 22 142 L28 142 Q30 138 28 134 L34 110 Z"
            fill="url(#mv-unicorn)"
          />
          <path
            d="M40 105 L36 138 Q36 142 40 142 L46 142 Q48 138 46 134 L50 112 Z"
            fill="url(#mv-unicorn)"
          />
          <path
            d="M-2 70 Q4 50 28 50 Q56 50 70 70 Q78 84 72 100 Q60 110 36 108 Q14 106 2 96 Q-8 84 -2 70 Z"
            fill="url(#mv-unicorn)"
          />
          <path d="M62 56 Q66 36 64 18 Q66 14 70 16 Q72 36 70 60 Z" fill="url(#mv-unicorn)" />
          <path d="M52 60 Q58 42 60 26 Q62 22 66 24 Q68 44 62 64 Z" fill="url(#mv-unicorn)" />
          <path
            d="M58 56 Q70 40 86 30 Q98 24 102 14 Q104 10 100 8 Q90 14 84 22 Q76 26 72 34 Q60 42 56 56 Z"
            fill="url(#mv-unicorn)"
          />
          <path d="M92 14 L96 4 L100 12 Z" fill="#0b0710" />
          <path
            d="M62 38 Q70 32 80 30 Q86 32 88 38 Q82 38 76 42 Q70 44 64 46 Z"
            fill="url(#mv-mane)"
          />
          <path
            d="M50 48 Q56 42 64 44 Q68 50 62 56 Q56 56 52 54 Z"
            fill="url(#mv-mane)"
            opacity="0.85"
          />
          <path
            d="M44 70 Q52 62 60 64 Q64 72 56 78 Q48 76 44 70 Z"
            fill="url(#mv-mane)"
            opacity="0.75"
          />
          <path d="M97 12 L102 -20 L107 12 Z" fill="url(#mv-horn)" />
          <path d="M101 -14 L102 -20 L103 -14 Z" fill="#f6efff" />
          <circle cx="92" cy="22" r="2" fill="#d4365e">
            <animate attributeName="opacity" values="1;0.4;1" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="92" cy="22" r="3.5" fill="rgba(212, 54, 94, 0.55)" />
          <ellipse cx="34" cy="146" rx="22" ry="3" fill="rgba(212, 54, 94, 0.35)" />
        </g>

        {/* Bat arc on the left */}
        <g fill="#0b0710" transform="translate(80 320)">
          <path d="M0 0 q-6 -5 -12 0 q4 2 6 6 q2 -4 6 -6 q4 2 6 6 q2 -4 6 -6 q-6 -5 -12 0 z" opacity="0.85" />
          <path d="M40 -30 q-5 -4 -10 0 q3 2 5 5 q2 -3 5 -5 q3 2 5 5 q2 -3 5 -5 q-5 -4 -10 0 z" opacity="0.75" />
          <path d="M88 -50 q-5 -4 -10 0 q3 2 5 5 q2 -3 5 -5 q3 2 5 5 q2 -3 5 -5 q-5 -4 -10 0 z" opacity="0.7" />
          <path d="M140 -56 q-4 -3 -8 0 q3 1 4 4 q1 -3 4 -4 q3 1 4 4 q1 -3 4 -4 q-4 -3 -8 0 z" opacity="0.7" />
        </g>
      </svg>

      <div className="mv-scene__vignette" />

      <div className="mv-scene__cards">
        {received.map((entry, i) => {
          const slot = WEB_SLOTS[i % WEB_SLOTS.length]!;
          // When the user receives more than one cycle's worth of gumballs,
          // nudge later cycles a few percent so they don't stack pixel-perfect
          // on top of earlier ones.
          const cycle = Math.floor(i / WEB_SLOTS.length);
          return (
            <GumballCard
              key={entry.id}
              entry={entry}
              index={i}
              left={slot.left + cycle * 1.2}
              top={slot.top + cycle * 1.4}
            />
          );
        })}
      </div>
    </div>
  );
}

function GumballCard({
  entry,
  index,
  left,
  top
}: {
  entry: ReceivedGumball;
  index: number;
  left: number;
  top: number;
}) {
  const count = Math.max(1, Math.min(entry.amount, 8));
  const columns = Math.min(count, 4);
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);
  return (
    <div className="mv-card" style={{ left: `${left}%`, top: `${top}%` }}>
      {/* Faint silk halo behind the cluster — sells the "caught in the web" read. */}
      <div className="mv-card__silk" aria-hidden="true" />
      <div
        className="mv-card__cluster"
        style={{ gridTemplateColumns: `repeat(${columns}, 18px)` }}
      >
        {pieces.map((i) => (
          <Gumball key={i} variant={(index + i) % 3} index={index * 7 + i} />
        ))}
      </div>
      <div className="mv-card__tooltip" role="tooltip">
        <span className="mv-card__from">{entry.from}</span>
        <span className="mv-card__amount">{entry.amount}</span>
        {entry.reason !== null && entry.reason.trim() !== "" && (
          <div className="mv-card__note">{entry.reason}</div>
        )}
      </div>
    </div>
  );
}

function Gumball({ variant, index }: { variant: number; index: number }) {
  // Three goth palettes: oxblood, moonlight silver-violet, dusk violet.
  const palettes: ReadonlyArray<readonly [string, string]> = [
    ["#d4365e", "#6b1029"],
    ["#c8b8d6", "#5b2e63"],
    ["#9f7aff", "#3a1c45"]
  ];
  const [c1, c2] = palettes[variant % palettes.length]!;
  const id = `mv-gb-${index}`;
  return (
    <svg
      className="mv-card__piece"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={id} cx="32%" cy="28%" r="80%">
          <stop offset="0%" stopColor="#f6efff" />
          <stop offset="32%" stopColor={c1} />
          <stop offset="100%" stopColor={c2} />
        </radialGradient>
      </defs>
      <circle cx="10" cy="10" r="9" fill={`url(#${id})`} />
      <ellipse cx="6.5" cy="6" rx="3" ry="2.4" fill="rgba(246, 239, 255, 0.7)" />
      <circle cx="14" cy="14.5" r="1" fill="rgba(246, 239, 255, 0.55)" />
    </svg>
  );
}
